// api.js

import axios from 'axios';
import { getAuth } from 'firebase/auth';

// Create a dedicated Axios instance for your API
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://library-admin-478409.et.r.appspot.com/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Use an interceptor to automatically add the JWT to every outgoing request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const explicitBranch = config.params?.branchId || config.data?.branchId;
    const selectedBranchId = explicitBranch || localStorage.getItem('librarydesk.selectedBranchId');
    if (selectedBranchId) {
      config.headers['X-Branch-Id'] = selectedBranchId;
    }
    // Let Axios auto-set Content-Type for FormData (multipart/form-data with boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses — silently refresh JWT before logging out
let isRefreshing = false;
let refreshPromise = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // The backend returns 403 (rather than 401) when its JWT has expired or
    // was invalidated by a signing-key change. Refresh only those auth errors;
    // an unrelated 403 must still reach the caller as a permission failure.
    const invalidSession = error.response?.status === 403 &&
      /Invalid or expired token|token does not identify an account/i.test(error.response?.data?.error || '');

    // Skip requests already retried and the token exchange itself.
    if (
      (error.response?.status === 401 || invalidSession) &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/users/auth')
    ) {
      originalRequest._retry = true;

      // If already refreshing, wait for that to finish
      if (isRefreshing) {
        try {
          await refreshPromise;
          originalRequest.headers.Authorization = `Bearer ${localStorage.getItem('accessToken')}`;
          return api(originalRequest);
        } catch {
          return Promise.reject(error);
        }
      }

      isRefreshing = true;
      refreshPromise = (async () => {
        try {
          const auth = getAuth();
          const currentUser = auth.currentUser;
          if (!currentUser) throw new Error('No Firebase user');

          // Get a fresh Firebase token and re-authenticate with backend
          const idToken = await currentUser.getIdToken(true);
          const { data } = await axios.post(
            `${api.defaults.baseURL}/users/auth`,
            { idToken },
            { headers: { 'Content-Type': 'application/json' } }
          );
          localStorage.setItem('accessToken', data.accessToken);
        } finally {
          isRefreshing = false;
        }
      })();

      try {
        await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${localStorage.getItem('accessToken')}`;
        return api(originalRequest);
      } catch {
        // Refresh truly failed — now logout
        localStorage.removeItem('accessToken');
        localStorage.removeItem('librarydesk.localQaSession');
        window.location.href = '/signin';
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
